import { Alert, Box, Header, Link, SpaceBetween, TextContent } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import constants from '../../common/constants';
import Breadcrumbs from '../common/Breadcrumbs';
import * as UtilApi from '../../api/UtilsApi';
import '../../resources/styles/scss/components/apgl-user-profile/apgl-user-profile-form.css';
import AppContextProps from '../../contexts/AppContextProps';
import { withAppContext } from '../../contexts/AppContext';
import {
  ExpertiseItem,
  FormErrorType,
  OtherPublicationItem,
  PublicationItem,
} from './APGLUserProfileUtils';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import {
  AWSService,
  AWSServiceExperience,
  ContentWorkflow,
  ExternalPublicationLink,
  APGLUserProfileAttributes,
  TechDomain,
  TechDomainExperience,
  Publication,
} from '@amzn/awscat-pattern-library-service-model';
import BrowserHistory from '../utils/BrowserHistory';
import LoadingPage from '../common/LoadingPage';
import APGLUserProfileEditingControls from './APGLUserProfileEditingControls';
import APGLUserProfileFormBody from './APGLUserProfileFormBody';

let mplServiceClient: IMplServiceClient;
let publicationNameMap: Map<string, string>;
let reviewNameMap: Map<string, string>;

const APGLUserProfileEditingForm: React.FunctionComponent<AppContextProps> = ({
  appContext,
}: AppContextProps) => {
  const { authnInfo } = appContext;
  // @ts-expect-error TS2339 - strict migration
  const { userProfile } = authnInfo;
  const { userId } = userProfile;

  /* states */

  // profile existence check
  const [registered, setRegistered] = useState<boolean>(true);
  const [loadingUserInfo, setLoadingUserInfo] = useState<boolean>(true);

  // description states
  const [description, setDescription] = useState<string>('');

  // peer reviewer option states
  const [isPeerReviewer, setIsPeerReviewer] = useState<boolean>(true);

  // expertise states
  const [techDomainExperiences, setTechDomainExperiences] = useState<TechDomainExperience[]>();
  const [awsServiceExperiences, setAwsServiceExperiences] = useState<AWSServiceExperience[]>();

  // authored content states
  const [allPubs, setAllPubs] = useState<string[]>([]);
  const [authoredPubs, setAuthoredPubs] = useState<string[]>([]); // selected authored publications

  // reviewed content states
  const [allReviews, setAllReviews] = useState<string[]>([]);
  const [reviewedPubs, setReviewedPubs] = useState<string[]>([]); // selected reviewed publications

  // other publication states
  const [otherPubs, setOtherPubs] = useState<ExternalPublicationLink[]>();

  // validation error states
  const [formError, setFormError] = useState<Map<FormErrorType, string>>(
    new Map<FormErrorType, string>(),
  );
  const [errorVisible, setErrorVisible] = useState<boolean>(false);

  // update profile states
  const [profileModified, setProfileModified] = useState<boolean>(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState<boolean>(false);
  const [updateProfileError, setUpdateProfileError] = useState<string>('');

  /* initialize */
  useEffect(() => {
    mplServiceClient = MplServiceClientFactory.create();
    publicationNameMap = new Map<string, string>();
    reviewNameMap = new Map<string, string>();

    // previously saved information entered by user
    async function loadAPGLUserProfile(allAPGLPublications: Publication[]) {
      try {
        const allAPGLPublicationIds = allAPGLPublications.map((p) => p.contentId);
        const { apglUserProfile } = await mplServiceClient.getAPGLUserProfile(userId);
        const {
          description: upDescription,
          isPeerReviewer: upIsPeerReviewer,
          techDomains: upTechDomains,
          awsServices: upAwsServices,
          apgPublications,
          reviewedPublications,
          otherPublications,
        } = apglUserProfile;
        setDescription(upDescription);
        setIsPeerReviewer(upIsPeerReviewer);
        setTechDomainExperiences(upTechDomains);
        setAwsServiceExperiences(upAwsServices);
        setAuthoredPubs(apgPublications.filter((pub) => allAPGLPublicationIds.includes(pub)));
        setReviewedPubs(reviewedPublications.filter((pub) => allAPGLPublicationIds.includes(pub)));
        setOtherPubs(otherPublications);
        setRegistered(true);
      } catch (error) {
        // @ts-expect-error TS18046 - strict migration
        if (error.response.status === 404) {
          setRegistered(false);
        } else {
          throw error;
        }
      }
    }

    // all authored content names to select from
    async function loadAllPublications(allAPGLPublications: Publication[]) {
      // get authored workflows
      const { workflows: authorWorkflows } = await mplServiceClient.getContentsForAuthor(userId);
      const { workflows: coAuthorWorkflows } =
        await mplServiceClient.listSharedContentsForUser(userId);
      // filter published contents
      const publishedWorkflows: ContentWorkflow[] = [
        ...authorWorkflows,
        ...coAuthorWorkflows,
      ].filter((wf) => UtilApi.isPublishedWorkflow(wf));
      // populate content ID -> name map
      publishedWorkflows.map((wf) => {
        const contentName = allAPGLPublications?.find((pub) => pub.contentId === wf.contentId)
          ?.contentMetadata?.contentName;
        if (contentName) {
          publicationNameMap.set(wf.contentId, contentName); // set content name map
        }
      });
      setAllPubs(publishedWorkflows.map((wf) => wf.contentId));
    }

    // all reviewed content names to select from
    async function loadAllReviews(allAPGLPublications: Publication[]) {
      // list all contents reviewed by user, including those not published
      const { contentIds } = await mplServiceClient.listContentsReviewedByUser(userId);
      const reviewedPubIds: string[] = []; // published contents reviewed by user
      contentIds.map(async (cid) => {
        const publication = allAPGLPublications?.find((pub) => pub.contentId === cid);
        const contentName = publication?.contentMetadata?.contentName;
        const publicationState = publication?.publicationState;
        // only selectable if content is published
        if (contentName && publicationState && UtilApi.isPublishedWorkflowState(publicationState)) {
          reviewedPubIds.push(cid);
          reviewNameMap.set(cid, contentName);
        }
      });
      setAllReviews(reviewedPubIds);
    }

    async function loadAll() {
      setLoadingUserInfo(true);
      const { publications: allAPGLPublications } =
        await mplServiceClient.listPublicationsThroughS3();
      await loadAPGLUserProfile(allAPGLPublications);
      await loadAllPublications(allAPGLPublications);
      await loadAllReviews(allAPGLPublications);
      setLoadingUserInfo(false);
      setProfileModified(false);
    }
    loadAll();
  }, [userId]);

  /* breadcrumb */
  const breadcrumbItems = [
    { text: constants.APP_NAME_BREAD_CRUMB, href: '/' },
    { text: 'Edit user profile', href: '' },
  ];

  /* header */
  const header = <Header variant="h2">Edit user profile</Header>;

  /* not registered warning */
  const notRegisteredWarning = !registered ? (
    <Alert type="warning" header={'You have not created your APGL user profile yet.'}>
      <span>Please click </span>
      <span>
        <Link href="/user-profile-registration">here</Link> to create your user profile or{' '}
      </span>
      <span>
        <Link href="/">go back</Link> to home page.
      </span>
    </Alert>
  ) : null;

  /* form body */
  const onDescriptionChanged = (text: string) => {
    setDescription(text);
    setProfileModified(true);
    setErrorVisible(false);
  };

  const onPeerReviewerOptionChanged = (checked: boolean) => {
    setIsPeerReviewer(checked);
    setProfileModified(true);
  };

  const onTechDomainsChanged = (items: ExpertiseItem[]) => {
    const experiences = items.map((item) => {
      const { domainExpertise, yearsOfExperience, rank } = item;
      return TechDomainExperience.create(domainExpertise as TechDomain, yearsOfExperience, rank);
    });
    setTechDomainExperiences(experiences);
    setProfileModified(true);
    setErrorVisible(false);
  };

  const onAWSServicesChanged = (items: ExpertiseItem[]) => {
    const experiences = items.map((item) => {
      const { domainExpertise, yearsOfExperience, rank } = item;
      return AWSServiceExperience.create(domainExpertise as AWSService, yearsOfExperience, rank);
    });
    setAwsServiceExperiences(experiences);
    setProfileModified(true);
    setErrorVisible(false);
  };

  const onAuthoredPubsChanged = (pubs: PublicationItem[]) => {
    const pubIds = pubs.map((pub) => pub.contentId);
    setAuthoredPubs(pubIds);
    setProfileModified(true);
    setErrorVisible(false);
  };

  const onReviewedPubsChanged = (pubs: PublicationItem[]) => {
    const pubIds = pubs.map((pub) => pub.contentId);
    setReviewedPubs(pubIds);
    setProfileModified(true);
    setErrorVisible(false);
  };

  const onOtherPubsChanged = (pubs: OtherPublicationItem[]) => {
    const pubLinks = pubs.map((pub) => ExternalPublicationLink.create(pub.title, pub.url));
    setOtherPubs(pubLinks);
    setProfileModified(true);
    setErrorVisible(false);
  };

  const onFormErrorChanged = (error: Map<FormErrorType, string>) => {
    setFormError(error);
  };

  const formBody = registered ? (
    <APGLUserProfileFormBody
      publicationNameMap={publicationNameMap}
      reviewNameMap={reviewNameMap}
      upDescription={description}
      upIsPeerReviewer={isPeerReviewer}
      // @ts-expect-error TS2322 - strict migration
      upTechDomains={techDomainExperiences}
      // @ts-expect-error TS2322 - strict migration
      upAwsServices={awsServiceExperiences}
      apgPublications={authoredPubs}
      reviewedPublications={reviewedPubs}
      // @ts-expect-error TS2322 - strict migration
      otherPublications={otherPubs}
      allPubs={allPubs}
      allReviews={allReviews}
      appContext={appContext}
      handleDescriptionChanged={onDescriptionChanged}
      handlePeerReviewerOptionChanged={onPeerReviewerOptionChanged}
      handleTechDomainsChanged={onTechDomainsChanged}
      handleAWSServicesChanged={onAWSServicesChanged}
      handleAuthoredPubsChanged={onAuthoredPubsChanged}
      handleReviewedPubsChanged={onReviewedPubsChanged}
      handleOtherPubsChanged={onOtherPubsChanged}
      handleFormErrorChanged={onFormErrorChanged}
      initFormError={formError}
    />
  ) : null;

  const hasFormError = () => {
    return formError.size > 0;
  };

  const onUpdateProfile = async () => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
    if (hasFormError()) {
      setErrorVisible(true);
      return;
    }
    setIsUpdatingProfile(true);
    try {
      await mplServiceClient.updateAPGLUserProfile(
        userId,
        description,
        APGLUserProfileAttributes.create({
          isPeerReviewer,
          // @ts-expect-error TS18048 - strict migration
          techDomains: techDomainExperiences.filter((exp) => exp.techDomain),
          // @ts-expect-error TS18048 - strict migration
          awsServices: awsServiceExperiences.filter((exp) => exp.awsService),
          apgPublications: authoredPubs.filter((pub) => pub),
          reviewedPublications: reviewedPubs.filter((pub) => pub),
          // @ts-expect-error TS18048 - strict migration
          otherPublications: otherPubs.filter((pub) => pub.title),
        }),
      );
      setProfileModified(false);
    } catch (e) {
      // tslint:disable-next-line: no-console
      console.error(e);
      // @ts-expect-error TS18046 - strict migration
      setUpdateProfileError(e.message);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const onCancelUpdate = () => {
    BrowserHistory.push(`/`);
  };

  const generateFormError = () => {
    const errors = Array.from(formError.values());
    // @ts-expect-error TS2322 - strict migration
    let error: React.ReactElement =
      errorVisible && hasFormError() ? (
        <Alert type="error" header="The following errors exist in your user profile form:">
          <TextContent>
            <ul>
              {errors.map((errorText, idx) => (
                <li key={errorText}>{errorText}</li>
              ))}
            </ul>
            <p>Please fix these errors before updating your user profile.</p>
          </TextContent>
        </Alert>
      ) : null;
    if (updateProfileError) {
      error = <Alert type="error" header={constants.UPDATE_APGL_USER_PROFILE_ERROR} />;
    }
    return error;
  };

  const controlsSection = registered ? (
    <Box float="right">
      <APGLUserProfileEditingControls
        isUpdatingProfile={isUpdatingProfile}
        isUpdateEnabled={profileModified}
        handleUpdate={onUpdateProfile}
        handleCancel={onCancelUpdate}
      />
    </Box>
  ) : null;

  return loadingUserInfo ? (
    <LoadingPage />
  ) : (
    <div id="user-profile-registration">
      <Box margin={{ left: 'xxl', top: 'm', bottom: 'xxl', right: 'xxl' }}>
        <Breadcrumbs items={breadcrumbItems} />
        <Box margin={{ left: 'l', top: 'm', bottom: 'l', right: 'l' }}>
          <SpaceBetween direction="vertical" size="xl">
            {header}
            {notRegisteredWarning}
            {generateFormError()}
            {formBody}
            {controlsSection}
          </SpaceBetween>
        </Box>
      </Box>
    </div>
  );
};

export default withAppContext(APGLUserProfileEditingForm);
